import mixpanel from 'mixpanel-browser';
import { useCallback, useMemo, useRef, useState } from 'react';

import { areAnalyticsEnabledInEnv } from '../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../utils/useQueueUntilReady';
import type { Integration, MixpanelIntegration, PageViewProps } from './types';

const ARBITRARY_PAGEVIEW_EVENT_NAME = 'Loaded a Page';

const useMixpanelIntegration = (): MixpanelIntegration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const enabled = isMixpanelEnabled();

  const trackEvent = useCallback((eventName: string, props: Record<string, unknown>) => {
    if (!isMixpanelEnabled()) return;

    mixpanel.track(eventName, props);
  }, []);
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  const trackPageView = useCallback(({ path }: PageViewProps) => {
    if (!isMixpanelEnabled()) return;

    mixpanel.track_pageview({ path }, { event_name: ARBITRARY_PAGEVIEW_EVENT_NAME });
  }, []);
  const initAwareTrackPageView = useQueueUntilReady(trackPageView, isInitialized);

  const identifyUser = useCallback<NonNullable<Integration['identifyUser']>>(
    ({ commonData, mixpanelData }) => {
      if (!isMixpanelEnabled() || !mixpanelData) return;

      if (!mixpanelData.lastActivityAt) {
        mixpanel.alias(commonData.id);
      } else {
        mixpanel.identify(commonData.id);
      }
      mixpanel.people.set({
        $user_id: commonData.id,
        $name: commonData.fullName,
        $first_name: commonData.firstName,
        $last_name: commonData.lastName,
        $email: commonData.email,
        ...commonData,
      });
      mixpanel.register({
        ...commonData,
      });
    },
    [],
  );
  const initAwareIdentifyUser = useQueueUntilReady(identifyUser, isInitialized);

  const resetUserIdentity = useCallback<NonNullable<Integration['resetUserIdentity']>>(() => {
    if (!isMixpanelEnabled()) return;

    mixpanel.reset();
  }, []);
  const initAwareResetUserIdentity = useQueueUntilReady(resetUserIdentity, isInitialized);

  const initialize = useCallback(() => {
    if (!isMixpanelEnabled() || !process.env.MIXPANEL_TOKEN) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    const token = process.env.MIXPANEL_TOKEN;
    return new Promise<void>((resolve) => {
      mixpanel.init(token, {
        api_host: 'https://api-eu.mixpanel.com',
        cross_subdomain_cookie: true,
        loaded: () => {
          initializationState.current = 'done';
          setIsInitialized(true);
          resolve();
        },
        persistence: 'cookie',
      });
    });
  }, []);

  const getDeviceId = useCallback<MixpanelIntegration['getDeviceId']>(() => {
    if (!isMixpanelEnabled() || initializationState.current !== 'done') return undefined;

    const deviceId = mixpanel.get_property('$device_id');
    return deviceId ? String(deviceId) : undefined;
  }, []);

  return useMemo(
    () => ({
      name: 'mixpanel',
      enabled,
      initialize,
      trackEvent: initAwareTrackEvent,
      trackPageView: initAwareTrackPageView,
      identifyUser: initAwareIdentifyUser,
      resetUserIdentity: initAwareResetUserIdentity,
      getDeviceId,
    }),
    [
      enabled,
      initialize,
      initAwareTrackEvent,
      initAwareTrackPageView,
      initAwareIdentifyUser,
      initAwareResetUserIdentity,
      getDeviceId,
    ],
  );
};

const isMixpanelEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.MIXPANEL_TOKEN) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export function isMixpanelIntegration(
  integration: Integration,
): integration is MixpanelIntegration {
  return integration.name === 'mixpanel';
}

export default useMixpanelIntegration;
